import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d5j6hgeihetfqh7m5lk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#0F0F0F] pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div>
            <h3 className="text-2xl font-serif text-white mb-4">Stay Connected</h3>
            <p className="text-gray-400 mb-6 text-sm leading-relaxed">
              Subscribe to our newsletter for exclusive tips, industry insights, and success stories from the world of content creation.
            </p>
            <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-4 py-3 bg-transparent border-b-2 border-[#F6C1A1] text-white placeholder-gray-400 focus:border-[#F2A35E] focus:outline-none transition-colors duration-300 text-sm"
              />
              {submitStatus === 'success' && (
                <p className="text-green-400 text-xs">Thanks for subscribing!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-2.5 bg-[#F2A35E] text-white text-sm font-semibold rounded-full hover:bg-[#C77A5A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Quick Links</h4>
            <div className="space-y-3">
              <button onClick={() => scrollToSection('home')} className="block text-gray-400 hover:text-[#F2A35E] transition-colors duration-300 text-sm cursor-pointer">
                Home
              </button>
              <button onClick={() => scrollToSection('about')} className="block text-gray-400 hover:text-[#F2A35E] transition-colors duration-300 text-sm cursor-pointer">
                About Us
              </button>
              <button onClick={() => scrollToSection('methodology')} className="block text-gray-400 hover:text-[#F2A35E] transition-colors duration-300 text-sm cursor-pointer">
                Methodology
              </button>
              <button onClick={() => scrollToSection('offers')} className="block text-gray-400 hover:text-[#F2A35E] transition-colors duration-300 text-sm cursor-pointer">
                Offers
              </button>
              <button onClick={() => scrollToSection('questions')} className="block text-gray-400 hover:text-[#F2A35E] transition-colors duration-300 text-sm cursor-pointer">
                FAQ
              </button>
              <button onClick={() => scrollToSection('contact')} className="block text-gray-400 hover:text-[#F2A35E] transition-colors duration-300 text-sm cursor-pointer">
                Contact
              </button>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Contact Info</h4>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-[#F6C1A1] mb-1">Email</p>
                <a href="mailto:somosofm@venezia.work" className="text-gray-400 hover:text-[#F2A35E] transition-colors duration-300 text-sm">
                  somosofm@venezia.work
                </a>
              </div>
              <div>
                <p className="text-xs text-[#F6C1A1] mb-1">Phone</p>
                <a href="tel:+18001234567" className="text-gray-400 hover:text-[#F2A35E] transition-colors duration-300 text-sm">
                  +1 641 888 6214
                </a>
              </div>
              <div>
                <p className="text-xs text-[#F6C1A1] mb-1">Location</p>
                <p className="text-gray-400 text-sm">Caribbean Islands</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center space-x-6">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-[#F6C1A1] hover:text-[#F2A35E] transition-colors duration-300">
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-[#F6C1A1] hover:text-[#F2A35E] transition-colors duration-300">
                <i className="ri-twitter-x-line text-xl"></i>
              </a>
              <a href="https://telegram.org" target="_blank" rel="noopener noreferrer" className="text-[#F6C1A1] hover:text-[#F2A35E] transition-colors duration-300">
                <i className="ri-telegram-line text-xl"></i>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-[#F6C1A1] hover:text-[#F2A35E] transition-colors duration-300">
                <i className="ri-linkedin-line text-xl"></i>
              </a>
            </div>

            <p className="text-gray-500 text-sm">
              © 2024 Venezia Agency. All rights reserved.
            </p>

            <div className="flex items-center space-x-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm whitespace-nowrap">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm whitespace-nowrap">
                Terms of Service
              </a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm whitespace-nowrap">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
